// useListingDetails.js
// Custom hook for managing ListingDetails state and operations

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../services/firebase';
import { getSellerByListingId } from '../../utils/userUtils';
import { startDirect } from '../../utils/messageUtils';

const useListingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIndex, setImgIndex] = useState(0);

  // Fetch listing and seller data
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const listingRef = doc(db, "listings", id);
        const docSnap = await getDoc(listingRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setListing({ ...data, id });
          
          // Fetch seller details
          const sellerObj = await getSellerByListingId(id);
          setSeller(sellerObj);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  // Image navigation handlers
  const showPrev = () => {
    if (!listing?.images) return;
    setImgIndex((prev) => prev > 0 ? prev - 1 : listing.images.length - 1);
  };

  const showNext = () => {
    if (!listing?.images) return;
    setImgIndex((prev) => prev < listing.images.length - 1 ? prev + 1 : 0);
  };

  const selectImage = (i) => {
    setImgIndex(i);
  };

  // Handle messaging seller
  const handleMessageSeller = async () => {
    if (!user) {
      alert("Please log in to message the seller.");
      return;
    }

    if (!listing || !listing.id) {
      alert("Listing data not loaded. Please refresh the page.");
      return;
    }

    // Support both sellerId (production) and createdBy (legacy) field names
    const sellerUserId = listing.sellerId || listing.createdBy;
    
    // Check if seller ID is valid (should be a Firebase UID)
    if (!sellerUserId || sellerUserId === "yes" || sellerUserId.length < 10) {
      alert("This listing has invalid seller information. Please contact support or try another listing.");
      return;
    }

    if (sellerUserId === user.uid) {
      alert("You cannot message yourself.");
      return;
    }

    try {
      const chatId = await startDirect(user.uid, sellerUserId, listing.id);
      navigate(`/direct/${chatId}`);
    } catch (error) {
      alert("Unable to start conversation. Please try again.");
    }
  };

  // Check if current user is the seller
  const isOwner = user && listing && (listing.sellerId === user.uid || listing.createdBy === user.uid);

  return {
    listing,
    seller,
    loading,
    imgIndex,
    user,
    isOwner,
    showPrev,
    showNext,
    selectImage,
    handleMessageSeller
  };
};

export default useListingDetails;